/* eslint-disable no-restricted-syntax */
import SentryClient from '@/helpers/sentry';
import { DisciplineEvaluationAndDeadlines } from '@/models/evaluation-and-deadlines/evaluation-and-deadlines.model';
import { UndergradDiscipline } from '@/models/undergrad/undergrad-discipline.model';
import { UserMicrocertificate } from '@/store/modules/microcertificates/types';
import { ActiveStudyWindow } from '@/services/undergrad/pegasus-pepe-legal/user-grades.service';
import RootState from '@/store/root_state';
import { ListModel } from '@descomplica/dex-models';
import { ActionTree } from 'vuex';
import {
  GradeWorthyActivitieDiscipline,
  GradeWorthyActivities,
  GradeWorthyActivitiesState,
} from './state';

const actions: ActionTree<GradeWorthyActivitiesState, RootState> = {
  async PULL_GRADE_WORTHY_ACTIVITIES({ getters, dispatch, commit }): Promise<void> {
    try {
      await dispatch('PULL_ACTIVE_STUDY_WINDOWS');

      const currentStudyWindow: ActiveStudyWindow = getters.getCurrentStudyWindow;

      let undergradDisciplines: UndergradDiscipline[] = [];
      let studyWindowId = '';

      if (currentStudyWindow) {
        undergradDisciplines = (getters.undergradDisciplines as UndergradDiscipline[]).filter(
          (discipline) => discipline.studyWindowId === currentStudyWindow.id,
        );
        studyWindowId = currentStudyWindow.id;
      } else {
        undergradDisciplines = getters.currentDisciplinesByCycle as UndergradDiscipline[];
      }

      undergradDisciplines = undergradDisciplines.filter(
        (discipline) => !['Complementar', 'Eletiva'].includes(discipline.submoduleType),
      );

      if (undergradDisciplines.length < 1) {
        commit('SET_GRADE_WORTHY_ACTIVITIES', []);
        return;
      }

      const disciplineInstanceIds: string[] = [];
      const disciplineIds: string[] = [];

      const organizedDisciplinesInsideToSubmoduleMap = undergradDisciplines.reduce(
        (acc, discipline) => {
          const userMicrocertificates = getters.userMicrocertificates as UserMicrocertificate[];

          const microcertificate = userMicrocertificates.find(
            (certificate) => certificate.submoduleId === discipline.submoduleId,
          ) as UserMicrocertificate;

          const key = discipline.submoduleId;
          if (acc.has(key)) {
            const microcertificateAndDisciplines = acc.get(key) as GradeWorthyActivities;

            acc.set(key, {
              ...microcertificateAndDisciplines,
              disciplines: [...microcertificateAndDisciplines.disciplines, discipline],
            });
          } else {
            acc.set(key, {
              microcertificate,
              disciplines: [discipline],
            });
          }

          studyWindowId = studyWindowId || (discipline.studyWindowId as string);
          disciplineIds.push(discipline.id);

          if (discipline.disciplineInstanceId) {
            disciplineInstanceIds.push(discipline.disciplineInstanceId);
          }

          return acc;
        },
        new Map<string, GradeWorthyActivities>(),
      );

      if (disciplineInstanceIds.length < 1 || !studyWindowId) {
        commit('SET_GRADE_WORTHY_ACTIVITIES', []);
        return;
      }

      await dispatch('PULL_EVALUATION_AND_DEADLINES', {
        disciplineInstanceIds,
        studyWindowId,
      });

      const array = Array.from(organizedDisciplinesInsideToSubmoduleMap.values());

      const microcertificateAndDisciplines: GradeWorthyActivities[] = [];

      for (const activities of array) {
        const disciplines: GradeWorthyActivitieDiscipline[] = [];

        for (const discipline of activities.disciplines) {
          // eslint-disable-next-line no-await-in-loop
          await dispatch('PULL_DISCIPLINE_INSTANCE_INFO', {
            userId: getters.currentUser.id,
            disciplineId: discipline.id,
            courseId: getters.currentUndergradCourse.id,
          });

          const evaluation = (
            getters.getEvaluationAndDeadlines as DisciplineEvaluationAndDeadlines[]
          ).find(
            (evalAndDeadlines) =>
              evalAndDeadlines.disciplineInstanceId === discipline.disciplineInstanceId,
          ) as DisciplineEvaluationAndDeadlines;

          const reviewLists = getters.allLists as ListModel[];

          disciplines.push({
            ...discipline,
            reviewLists,
            activities: evaluation?.evaluationModel.deliverables,
            exercisesDeadlines: evaluation.exercisesDeadlines,
            grade: evaluation.grade,
          });
        }

        microcertificateAndDisciplines.push({
          microcertificate: activities.microcertificate,
          disciplines,
        });
      }

      commit('SET_GRADE_WORTHY_ACTIVITIES', microcertificateAndDisciplines);
    } catch (error: unknown) {
      commit('SET_GRADE_WORTHY_ACTIVITIES', []);
      SentryClient.captureError(error);
    }
  },
};

export default actions;
